import React, { useEffect } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useRouter } from 'next/compat/router';
import { useMutation } from '@apollo/client';

import { SmallLoader } from 'shared/components';
import useErnie from 'shared/hooks/use-ernie';
import { isReferralsRoute as isReferralsRouteUtils } from 'src/components/loyalty/rewards/dpos-utils';
import { detectLoyaltyIntegration, useLoyalty, Statuses } from 'src/components/loyalty/use-loyalty';
import { DialogContainer } from 'src/components/modals/components/simple-dialog';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { FindOrCreateReferralCodeDocument } from 'src/gql/graphql';
import { Connect, ReferralConnect } from './connect';
import { Pin } from './pin';
import { Success } from './success';
import { Terms } from './terms';

type LoyaltyProps = {
  onClose: (result?: { status: Statuses }) => void;
};

export const Loyalty = ({ onClose }: LoyaltyProps): JSX.Element => {
  const { dispensary } = useDispensary();
  const flags = useFlags();

  const isShowReferralsEnabled = flags['pos.customers.refer-a-friend.rollout'] ?? false;

  // Check if we're on a referrals route
  const router = useRouter();
  const showErnie = useErnie();
  const isReferralsRoute = router ? isReferralsRouteUtils(router) : false;
  const referralCode = router?.isReady ? String(router.query.code) : '';

  const [findOrCreateReferralCodeMutation, { data: findOrCreateReferralCodeData }] = useMutation(
    FindOrCreateReferralCodeDocument
  );

  useEffect(() => {
    if (isShowReferralsEnabled && isReferralsRoute && referralCode) {
      const fetchReferralData = async (): Promise<void> => {
        try {
          await findOrCreateReferralCodeMutation({
            variables: {
              dispensaryId: dispensary.id,
              lspName: dispensary.dposLspName ?? '',
              code: referralCode,
            },
          });
        } catch (error) {
          // Handle error silently or log it
          showErnie('Error fetching referral data. Please try again.', 'danger');
        }
      };
      void fetchReferralData();
    }
  }, [
    isShowReferralsEnabled,
    dispensary.dposLspName,
    dispensary.id,
    findOrCreateReferralCodeMutation,
    referralCode,
    isReferralsRoute,
    showErnie,
  ]);

  const { status, setStatus, programName, programDescription, createUserAndPin, processPin, resendPin } = useLoyalty({
    initialStatus: Statuses.signup,
    integration: detectLoyaltyIntegration(dispensary),
    referralData: findOrCreateReferralCodeData?.findOrCreateReferralCode ?? null,
  });

  const handleClose = (): void => {
    onClose({ status });
  };
  return (
    <DialogContainer>
      {status === Statuses.loading && <SmallLoader />}
      {status === Statuses.signup &&
        (isShowReferralsEnabled && isReferralsRoute ? (
          <ReferralConnect
            onClose={onClose}
            setStatus={setStatus}
            programName={programName}
            programDescription={programDescription}
          />
        ) : (
          <Connect
            onClose={handleClose}
            setStatus={setStatus}
            programName={programName}
            programDescription={programDescription}
          />
        ))}

      {status === Statuses.terms && (
        <Terms onClose={handleClose} programName={programName} createUserAndPin={createUserAndPin} />
      )}
      {[Statuses.pin, Statuses.incorrectPin].includes(status) && !isReferralsRoute && (
        <Pin
          onClose={handleClose}
          status={status}
          programName={programName}
          processPin={processPin}
          resendPin={resendPin}
        />
      )}
      {status === Statuses.success && !isReferralsRoute && <Success onClose={handleClose} programName={programName} />}
    </DialogContainer>
  );
};
